import React, { useEffect, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import { useTranslation } from "react-i18next";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import { AhuType } from "../../../models/unitType";

function Filtering() {
  const { control, watch, setValue, register } = useFormContext();
  const { t } = useTranslation();

  // Watch filteringAdvancedSettings to conditionally show return filter
  const filteringAdvancedSettings = watch("filteringAdvancedSettings") || false;
  const ahuType = watch("Type");
  const isCompact = ahuType === AhuType.COMPACT || ahuType === AhuType.COMPACT_VERTICAL;
    
  // Fresh air filter class options
  const freshAirFilterClassOptions = useMemo(() => {
    return !isCompact
      ? [
            { value: "M5", label: "ePM10 60% (M5) / Energy class E" },
            { value: "M6", label: "ePM10 60% (M6) / Energy class E" },

        ]
      : [
          {
            value: "M6",
            label: "ePM10 70% (M6)",
          },
          {
            value: "F7",
            label: "ePM1 60% (F7)",
          },
          {
            value: "F8",
            label: "ePM1 70% (F8)",
          },
          {
            value: "F9",
            label: "ePM1 80% (F9)",
          },
        ];
  }, [isCompact]);

    // Supply air filter class options
  const supplyAirFilterClassOptions = [
          { value: "F7", label: "ePM1 60% (F7) / Energy class A+" },
          { value: "F9", label: "ePM1 85% (F9) / Energy class C" },
  ];
  // Return air filter class options
  const returnAirFilterClassOptions = useMemo(() => {
    return !isCompact
      ? [
          { value: "M5", label: "ePM10 60% (M5) / Energy class E" },
          { value: "M6", label: "ePM10 60% (M6) / Energy class E" },
        ]
      : [
          {
            value: "M6",
            label: "ePM10 70% (M6)",
          },
          {
            value: "F7",
            label: "ePM1 60% (F7)",
          },
          {
            value: "F8",
            label: "ePM1 70% (F8)",
          },
          {
            value: "F9",
            label: "ePM1 80% (F9)",
          },
        ];
  }, [isCompact]);

  useEffect(() => {
    if (isCompact) {
      setValue("fineFilterType", "Rigid bag filter");
    } else {
      setValue("fineFilterType", "Soft bag filter");
    }
  }, [isCompact, ahuType, setValue]);

  return (
    <Container component="main" sx={{ mb: 4, "min-width": "100%" }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" }, align: "center" }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}>
            {t("mainform.filtering")}
          </Typography>
        </Box>
        <Box
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <Typography component="h2" variant="h5">
            {t("filtering.fine")}
          </Typography>

          <TextField
            {...register("fineFilterType")}
            label={t("filteringTextFields.label")}
            name="fineFilterType"
            focused
            disabled
          />
          <Divider sx={{ m: 1 }} />

          <Controller
            name="filteringAdvancedSettings"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={t("filtering.advanced")}
                labelPlacement="end"
              />
            )}
          />

          {filteringAdvancedSettings && (
            <>
              {/* Fresh air filter class */}

              <Controller
                name="freshAirFilterClass"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      id="select-label-fresh"
                      sx={{ mt: 1, color: "#373437" }}
                    >
                      {t("filtering.freshFilter")}
                    </InputLabel>
                    <Select
                      {...field}
                      disablePortal
                      MenuProps={{ disableScrollLock: true }}
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      labelId="select-label-fresh"
                      label={t("filtering.freshFilter")}
                    >
                      {freshAirFilterClassOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  )}
              />

              {/* Supply air filter class */}
              {!isCompact && (
                <Controller
                  name="supplyAirFilterClass"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id="select-label-supply"
                        sx={{ mt: 1, color: "#373437" }}
                      >
                        {t("filtering.supplyFilter")}
                      </InputLabel>
                      <Select
                        {...field}
                        disablePortal
                        MenuProps={{ disableScrollLock: true }}
                        fullWidth
                        sx={{ mt: 1, mb: 1 }}
                        labelId="select-label-supply"
                        label={t("filtering.supplyFilter")}
                      >
                        {supplyAirFilterClassOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}

              {/* Return air filter class - shown when advanced settings is enabled */}

              <Controller
                name="returnAirFilterClass"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      id="select-label-return"
                      sx={{ mt: 1, color: "#373437" }}
                    >
                      {t("filtering.returnFilter")}
                    </InputLabel>
                    <Select
                      {...field}
                      disablePortal
                      MenuProps={{ disableScrollLock: true }}
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      labelId="select-label-return"
                      label={t("filtering.returnFilter")}
                    >
                      {returnAirFilterClassOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default Filtering;
